import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Download, Images, Maximize, Minimize, X } from 'lucide';

// MUI
import { Box, Button, IconButton, ThemeProvider, Tooltip, Typography } from '@mui/material';

// common
import Icon from 'common/components/Icon';
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import NotFoundBox from 'common/components/NotFoundBox';
import useService from 'common/hook/useService';
import useSchoolYearMonthFilter from 'common/hook/useSchoolYearMonthFilter';
import useIsGuest from 'common/hook/useIsGuest';
import useGetRole from 'common/hook/useGetRole';
import { buildResourceSearchStr } from 'common/utils/schoolYearMonths';
import { MUITheme } from 'config/setupTheme.ts';
import { TemmplateSettings } from 'svg';
import exportAsPng from 'libraries/html-to-png';

// screen
import { ClientModel, MMapNutritionMenuModel } from 'modules/map/types';
import {
  DailyDishFilterModel,
  DailyDishStatusEnum,
  defaultSettings,
} from 'modules/map/components/daily-dish/types';
import NewTemplate from 'modules/map/components/daily-dish/templates/NewTemplate';
import TemplateSettingsPanel from 'modules/map/components/daily-dish/components/template-settings-panel';
import { settingsReducer } from 'modules/map/components/daily-dish/reducer';
import ExportingImage from 'modules/map/components/daily-dish/components/ExportingImage';
import Filter from 'modules/map/components/daily-dish/components/form/Filter';
import { pickAbridgedMenu } from 'modules/map/components/daily-dish/pickAbridgedMenu';
import SampleMealPhotosDialog from 'modules/map/components/daily-dish/sample-meal/SampleMealPhotosDialog';
import { resolveSampleMealWeekStart } from 'modules/map/components/daily-dish/sample-meal/weekStart';
import { KIOS_STORAGE } from 'api/API_App';
import {
  imagesForWindow,
  pickWeeklyLook,
  resolveWindowId,
  cycleWeeklyLook,
} from 'modules/map/components/daily-dish/look/pickWeeklyLook';
import {
  clearLookStorage,
  coerceStoredSettings,
  persistLookRecord,
  readLookStorage,
  stripFavorites,
  writeLookStorage,
} from 'modules/map/components/daily-dish/look/storage';
import {
  deleteLocalBackground,
  getLocalBackground,
  putLocalBackground,
  resizeBackgroundFile,
  validateBackgroundFile,
} from 'modules/map/components/daily-dish/look/localBackgrounds';

interface Props {
  clientData: ClientModel;
  open?: boolean;
  onClose?: () => void;
}

/** Khung thiết kế gốc của mẫu thực đơn. Màn nhỏ co tỷ lệ này cho vừa, không cắt phần dưới. */
const ARTBOARD_WIDTH = 1920;
const ARTBOARD_HEIGHT = 1080;

const DailyDishDialog = (props: Props) => {
  const { clientData, open, onClose } = props;
  const service = useService();
  const isGuest = useIsGuest();
  const { abridgedMenu } = useGetRole(clientData);
  const canViewSampleMeals = abridgedMenu && !isGuest;

  // Ô Tuần lấy từ khung MenuPlan đã chốt. Món lấy riêng theo mã trường.
  const [filter, setFilter] = useState<DailyDishFilterModel>({
    state: DailyDishStatusEnum.propose,
  });
  const { monthOptions, weekOptions, framesFailed, schoolYear } = useSchoolYearMonthFilter(
    filter,
    setFilter
  );
  const orgId = clientData.operationCode;
  // Không gắn tiến độ vào khóa tìm. Bản MenuPlan đẩy sang có thể là xác nhận, không phải đề xuất.
  const searchStr = useMemo(
    () =>
      filter.week
        ? buildResourceSearchStr({
            monthKey: filter.month,
            week: filter.week,
            day: 0,
          })
        : null,
    [filter.month, filter.week]
  );

  const {
    data: weekMenus,
    isFetching,
    isPlaceholderData,
    isError,
  } = useQuery<MMapNutritionMenuModel[]>({
    queryKey: [searchStr, orgId, 'daily-dish'],
    // App đặt keepPreviousData toàn cục. Tắt ở đây để đổi trường không giữ thực đơn trường vừa xem.
    placeholderData: undefined,
    queryFn: async () => {
      if (!service || !searchStr || !orgId) return [];
      const json = await service.ClientAbridgedMenuController_list({
        params: { orgId, mode: 'real' },
        queries: { limit: 50, offset: 0, searchStr },
      });
      return (Array.isArray(json) ? json : []) as unknown as MMapNutritionMenuModel[];
    },
    enabled: (open ?? false) && !!service && !!searchStr && !!orgId,
  });

  const menus = useMemo(
    () => (!isPlaceholderData && !isError && weekMenus ? weekMenus : []),
    [isError, isPlaceholderData, weekMenus]
  );
  const menuData = useMemo(
    () => pickAbridgedMenu(menus, filter.state),
    [filter.state, menus]
  );

  useEffect(() => {
    const pickedState = menuData?.metadata?.v20241121?.state;
    if (!pickedState || pickedState === filter.state) return;
    setFilter((prev) => ({ ...prev, state: pickedState as DailyDishStatusEnum }));
  }, [filter.state, menuData]);

  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [sampleMealOpen, setSampleMealOpen] = useState(false);

  const weekStart = useMemo(
    () =>
      resolveSampleMealWeekStart(
        typeof filter.week === 'number' ? filter.week : undefined,
        weekOptions
      ),
    [filter.week, weekOptions]
  );

  const [templateSettings, dispatchSettings] = useReducer(settingsReducer, defaultSettings);
  const [lockedTheme, setLockedTheme] = useState<string | null>(null);
  const [lookOffset, setLookOffset] = useState<number | null>(null);
  const [localBackgroundUrl, setLocalBackgroundUrl] = useState<string | null>(null);
  const skipPersistRef = useRef(true);
  const { enqueueSnackbar } = useSnackbar();

  const selectedWeek = useMemo(
    () => weekOptions.find((week) => week.id === filter.week),
    [weekOptions, filter.week]
  );
  const dateFrom = selectedWeek?.date_from;
  const dateTo = selectedWeek?.date_to;

  const previewImages = useMemo(() => {
    if (!dateFrom || !dateTo) {
      return imagesForWindow('general');
    }
    const windowId = lockedTheme ?? resolveWindowId(dateFrom, dateTo, null);
    return imagesForWindow(windowId);
  }, [dateFrom, dateTo, lockedTheme]);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    const hydrate = async () => {
      skipPersistRef.current = true;
      if (!dateFrom || !dateTo) {
        dispatchSettings({ type: 'HYDRATE', settings: defaultSettings });
        setLockedTheme(null);
        setLookOffset(null);
        setLocalBackgroundUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        return;
      }

      const stored = readLookStorage(dateFrom);
      const lock = stored?.lockedTheme ?? null;
      setLockedTheme(lock);
      setLookOffset(stored?.lookOffset ?? null);
      const seed = pickWeeklyLook({
        dateFrom,
        dateTo,
        lockedTheme: lock,
        lookOffset: stored?.lookOffset ?? 0,
      });
      const restored = stored ? coerceStoredSettings(stored.settings, seed) : null;
      dispatchSettings({ type: 'HYDRATE', settings: restored ?? seed });

      if (stored?.localBackground) {
        try {
          const blob = await getLocalBackground(dateFrom);
          if (cancelled) {
            return;
          }
          if (blob) {
            createdUrl = URL.createObjectURL(blob);
            setLocalBackgroundUrl(createdUrl);
          } else {
            setLocalBackgroundUrl(null);
            dispatchSettings({ type: 'UPDATE_LOCAL_BACKGROUND', value: false });
          }
        } catch {
          if (!cancelled) {
            setLocalBackgroundUrl(null);
          }
        }
      } else {
        setLocalBackgroundUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      }
      skipPersistRef.current = false;
    };

    void hydrate();
    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (skipPersistRef.current || !dateFrom || !dateTo) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      // Phải cùng lookOffset với look đang hiện, nếu không persistLookRecord luôn thấy khác seed
      // và ghi nguyên cục settings cho tuần chỉ mới xoay preset.
      const seed = pickWeeklyLook({ dateFrom, dateTo, lockedTheme, lookOffset: lookOffset ?? 0 });
      const record = persistLookRecord({
        lockedTheme,
        localBackground: templateSettings.general.localBackground,
        current: templateSettings,
        seed,
        lookOffset,
      });
      if (!record) {
        clearLookStorage(dateFrom);
        return;
      }
      writeLookStorage(dateFrom, record);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [templateSettings, lockedTheme, lookOffset, dateFrom, dateTo]);

  const handleLockTheme = useCallback(
    (themeId: string | null) => {
      if (!dateFrom || !dateTo) {
        return;
      }
      skipPersistRef.current = true;
      setLockedTheme(themeId);
      setLookOffset(null);
      const seed = pickWeeklyLook({ dateFrom, dateTo, lockedTheme: themeId });
      dispatchSettings({ type: 'HYDRATE', settings: seed });
      setLocalBackgroundUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      if (themeId) {
        writeLookStorage(dateFrom, {
          lockedTheme: themeId,
          settings: null,
          localBackground: false,
          lookOffset: null,
        });
      } else {
        clearLookStorage(dateFrom);
      }
      skipPersistRef.current = false;
    },
    [dateFrom, dateTo]
  );

  const handleResetWeeklyLook = useCallback(() => {
    if (!dateFrom || !dateTo) {
      return;
    }
    skipPersistRef.current = true;
    setLockedTheme(null);
    setLookOffset(null);
    clearLookStorage(dateFrom);
    void deleteLocalBackground(dateFrom);
    setLocalBackgroundUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    dispatchSettings({
      type: 'HYDRATE',
      settings: pickWeeklyLook({ dateFrom, dateTo, lockedTheme: null }),
    });
    skipPersistRef.current = false;
  }, [dateFrom, dateTo]);

  const handleCyclePreset = useCallback(() => {
    if (!dateFrom || !dateTo) {
      return;
    }
    const next = cycleWeeklyLook({
      dateFrom,
      dateTo,
      lockedTheme,
      lookOffset,
    });
    setLookOffset(next.lookOffset);
    void deleteLocalBackground(dateFrom);
    setLocalBackgroundUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    dispatchSettings({ type: 'HYDRATE', settings: next.settings });
  }, [dateFrom, dateTo, lockedTheme, lookOffset]);

  const handleUploadBackground = useCallback(
    async (file: File) => {
      if (!dateFrom) {
        return;
      }
      const invalid = validateBackgroundFile(file);
      if (invalid) {
        enqueueSnackbar({ message: invalid, variant: 'error' });
        return;
      }
      try {
        const blob = await resizeBackgroundFile(file);
        const dropped = await putLocalBackground(dateFrom, blob);
        dropped.forEach((key) => clearLookStorage(key));
        skipPersistRef.current = true;
        dispatchSettings({ type: 'UPDATE_LOCAL_BACKGROUND', value: true });
        setLocalBackgroundUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        writeLookStorage(dateFrom, {
          lockedTheme,
          settings: stripFavorites({
            ...templateSettings,
            general: { ...templateSettings.general, localBackground: true },
          }),
          localBackground: true,
          lookOffset,
        });
        skipPersistRef.current = false;
      } catch (error) {
        const quota = error instanceof DOMException && error.name === 'QuotaExceededError';
        enqueueSnackbar({
          message: quota ? 'Máy hết chỗ lưu ảnh nền.' : 'Không lưu được ảnh nền.',
          variant: 'error',
        });
      }
    },
    [dateFrom, enqueueSnackbar, lockedTheme, lookOffset, templateSettings]
  );

  const contentToPrint = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(0.6);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Co mẫu 1920×1080 vừa vùng xem (kể cả toàn màn hình), để màn thấp không cắt món và không đè nút.
  useEffect(() => {
    const node = fullscreenContainerRef.current;
    if (!open || !node) return undefined;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      const width = Math.max(rect.width - 8, 0);
      const height = Math.max(rect.height - 8, 0);
      if (width < 1 || height < 1) return;
      setFitScale(Math.min(width / ARTBOARD_WIDTH, height / ARTBOARD_HEIGHT));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, menuData, showSettings, isFullscreen]);

  const handleDownloadPng = async () => {
    if (contentToPrint.current) {
      try {
        setIsExporting(true);
        const clientLogoUrl = clientData.logo ? `${KIOS_STORAGE}/${clientData.logo}` : undefined;
        await exportAsPng(contentToPrint.current, clientLogoUrl);
      } finally {
        setIsExporting(false);
      }
    }
  };

  const handleToggleSettings = () => {
    setShowSettings((prev) => !prev);
  };

  const handlePreviewFullScreen = () => {
    if (fullscreenContainerRef.current) {
      if (!document.fullscreenElement) {
        fullscreenContainerRef.current
          .requestFullscreen()
          .then(() => {
            setIsFullscreen(true);
          })
          .catch((err) => {
            console.error('Error attempting to enable fullscreen:', err);
          });
      } else {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        });
      }
    }
  };

  return (
    <MuiDialog
      open={open}
      maxWidth="desktop"
      fullWidth
      titleAlign="center"
      disablePortal
      disableCloseBtn
      PaperProps={{
        sx: {
          margin: 2,
          height: '100%',
          minHeight: 0,
          maxWidth: showSettings ? '1800px' : 'min(96vw, calc((100dvh - 300px) * 16 / 9))',
          display: 'flex',
          flexDirection: 'column',
          transition: 'max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* menu UI */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          }}
        >
          <Typography sx={{ textAlign: 'right', m: 2 }}>
            <IconButton onClick={onClose} size="small" color="primary" aria-label="Đóng">
              <Icon icon={X} size={16} />
            </IconButton>
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <Typography variant="h5" sx={{ textAlign: 'center', color: 'text.primary' }}>
              <FormattedMessage id="map.dailyDish" />
            </Typography>
          </Box>

          {/* Content area */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ px: 2, pt: 1 }}>
              <Filter
                filter={filter}
                setFilter={setFilter}
                monthOptions={monthOptions}
                weekOptions={weekOptions}
                framesFailed={framesFailed}
                trailing={
                  menuData || canViewSampleMeals ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      {canViewSampleMeals && (
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<Icon icon={Images} size={16} />}
                          disabled={!weekStart}
                          onClick={() => setSampleMealOpen(true)}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          <FormattedMessage id="map.sampleMealPhotos" />
                        </Button>
                      )}
                      {menuData && (
                        <Button
                          sx={{
                            height: '30px',
                            minWidth: 0,
                            backgroundColor: 'transparent !important',
                          }}
                          onClick={handleToggleSettings}
                        >
                          <TemmplateSettings height={'30px'} />
                        </Button>
                      )}
                    </Box>
                  ) : null
                }
              />
            </Box>
            <Box
              ref={fullscreenContainerRef}
              sx={{
                flex: 1,
                minHeight: 0,
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isFullscreen ? '#000' : 'transparent',
                '&:fullscreen': {
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#000',
                },
              }}
            >
              {/* Loading Overlay */}
              {isExporting && <ExportingImage />}
              {isFetching ? (
                <MuiLoading />
              ) : menuData ? (
                <ThemeProvider theme={MUITheme('light')}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      width: ARTBOARD_WIDTH,
                      height: ARTBOARD_HEIGHT,
                      transform: `translate(-50%, -50%) scale(${fitScale})`,
                      transformOrigin: 'center center',
                    }}
                  >
                    <Box ref={contentToPrint} sx={{ width: ARTBOARD_WIDTH, height: ARTBOARD_HEIGHT }}>
                      <NewTemplate
                        data={menuData}
                        clientData={clientData}
                        settings={templateSettings}
                        localBackgroundUrl={localBackgroundUrl}
                      />
                    </Box>
                  </Box>
                </ThemeProvider>
              ) : (
                <NotFoundBox />
              )}

              {/* fullscreen — góc vùng xem, không đè lên mẫu theo một chiều cao cố định */}
              {menuData && (
                <Box
                  sx={{
                    position: 'absolute',
                    right: 16,
                    bottom: 16,
                    zIndex: 2,
                  }}
                >
                  <Tooltip
                    title={
                      <FormattedMessage id={isFullscreen ? 'exitFullScreen' : 'openFullScreen'} />
                    }
                  >
                    <IconButton
                      size="large"
                      aria-label={isFullscreen ? 'Thoát toàn màn hình' : 'Xem toàn màn hình'}
                      onClick={handlePreviewFullScreen}
                      disabled={!menuData}
                      sx={{
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.65)',
                        },
                      }}
                    >
                      <Icon icon={isFullscreen ? Minimize : Maximize} size={40} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                p: 2,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                height: '72px',
              }}
            >
              <Button startIcon={<Icon icon={Download} size={16} />} onClick={handleDownloadPng} disabled={!menuData}>
                <FormattedMessage id="map.download" />
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Settings panel */}
        <TemplateSettingsPanel
          settings={templateSettings}
          dispatch={dispatchSettings}
          show={showSettings}
          lockedTheme={lockedTheme}
          previewImages={previewImages}
          schoolYear={schoolYear}
          onLockTheme={handleLockTheme}
          onResetWeeklyLook={handleResetWeeklyLook}
          onCyclePreset={handleCyclePreset}
          onUploadBackground={handleUploadBackground}
        />
      </Box>

      <SampleMealPhotosDialog
        open={sampleMealOpen}
        onClose={() => setSampleMealOpen(false)}
        siteCode={clientData.operationCode}
        weekStart={weekStart}
        menuItems={menuData?.structuredData?.data?.items}
      />
    </MuiDialog>
  );
};
export default DailyDishDialog;
